/**
 * Runtime (zod) schemas for every HTTP response DTO in api.ts.
 *
 * Used to generate the OpenAPI document (/api/docs) and available to clients
 * for response validation. Each schema is checked AT COMPILE TIME against its
 * TypeScript interface (see the `Same<>` assertions at the bottom), so the docs
 * cannot silently drift from what the backend returns.
 */
import { z } from "zod";
import { CalibrationType, CommandMode, CommandStatus, ConfigDeploymentStatus, ControlMode, Connectivity, IsoTime, MissionStatus, OperatingState, Pose2D, Role, Severity, Visibility, WaypointType, } from "./common.js";
import { API_ERROR_CODES, ArmJogVisibility, MapImportStatus, MapLayerKind, PointCloudFormat, ErrorDefinitionInput, ScopeType, SplineParams, WorkflowStep, } from "./api.js";
import { RegistryEntry } from "./registry.js";
import { CompatibilityReport } from "./robot-link.js";
import { PERMISSIONS } from "./permissions.js";
import { RoutePlanStatus } from "./route-plans.js";
const Id = z.string().uuid();
const Time = IsoTime;
const NullableTime = IsoTime.nullable();
const Json = z.unknown().describe("Arbitrary JSON");
const ShortError = z.object({ code: z.string(), message: z.string() });
export const ApiErrorBodySchema = z
    .object({
    requestId: z.string().describe("Echoed X-Request-Id; quote it when reporting issues"),
    errorCode: z.enum(API_ERROR_CODES),
    errorMessage: z.string(),
    errorData: Json.optional().describe("Details, e.g. zod issues for ValidationFailed"),
})
    .describe("Every non-2xx response uses this body");
export const pageOf = (item) => z.object({
    items: z.array(item),
    total: z.number().int(),
    page: z.number().int().describe("Zero-indexed"),
    size: z.number().int(),
});
// ── access ──
export const UserDtoSchema = z.object({ id: Id, email: z.string().email(), displayName: z.string(), isActive: z.boolean(), createdAt: Time });
export const AssignmentDtoSchema = z.object({ id: Id, role: Role, scopeType: ScopeType, scopeId: Id.nullable() });
export const UserWithAssignmentsSchema = UserDtoSchema.extend({ assignments: z.array(AssignmentDtoSchema) });
export const SessionResponseSchema = z.object({
    accessToken: z.string().describe("Bearer JWT, valid 15 minutes; refresh via POST /auth/refresh (httpOnly cookie)"),
    accessTokenExpiresAt: Time,
    user: UserDtoSchema,
    assignments: z.array(AssignmentDtoSchema),
});
export const MeDtoSchema = z.object({ user: UserDtoSchema, assignments: z.array(AssignmentDtoSchema), permissions: z.array(z.enum(PERMISSIONS)) });
export const ApiTokenDtoSchema = z.object({
    id: Id,
    name: z.string(),
    role: Role,
    scopeType: ScopeType,
    scopeId: Id.nullable(),
    expiresAt: Time,
    createdAt: Time,
    lastUsedAt: NullableTime,
    revokedAt: NullableTime,
});
export const ApiTokenCreatedSchema = ApiTokenDtoSchema.extend({ token: z.string().describe("`tcrpat_…` — shown exactly once") });
export const RealtimeTicketDtoSchema = z.object({ ticket: z.string(), expiresAt: Time });
// ── fleet ──
export const SiteDtoSchema = z.object({ id: Id, name: z.string(), timezone: z.string(), createdAt: Time });
export const FleetDtoSchema = z.object({ id: Id, siteId: Id, name: z.string(), robotCount: z.number().int(), createdAt: Time });
export const ControlOwnerDtoSchema = z.object({
    leaseId: Id,
    principalId: Id,
    principalName: z.string(),
    principalKind: z.enum(["user", "api_token"]),
    mode: ControlMode,
    expiresAt: Time,
    authorityGeneration: z.number().int().describe("Authority generation the lease was granted under"),
    state: z.enum(["pending_install", "active"]).describe("`pending_install` until the robot acknowledges the generation; nothing is dispatched under it before that"),
    installedAt: NullableTime,
});
export const RobotAuthorityDtoSchema = z.object({
    generation: z.number().int().describe("Current platform-issued authority generation"),
    installedGeneration: z.number().int().describe("Highest generation the robot acknowledged (0 = none)"),
    mode: ControlMode,
    pending: z.boolean().describe("Robot has not acknowledged `generation` yet; dispatch is held"),
});
export const RobotDtoSchema = z.object({
    id: Id,
    name: z.string(),
    serial: z.string(),
    model: z.string(),
    siteId: Id,
    fleetId: Id.nullable(),
    capabilities: z.array(z.string()),
    connectivity: Connectivity.describe("Link state, independent of the reported operating state"),
    lastSeenAt: NullableTime,
    reported: z
        .object({
        operatingState: OperatingState,
        rawState: z.string(),
        battery: z.object({ soc: z.number(), charging: z.boolean(), voltage: z.number().optional() }).nullable(),
        pose: Pose2D.nullable(),
        mapId: Id.nullable(),
        mapVersion: z.number().int().nullable().describe("Version of mapId the robot runs"),
        mapSource: z.enum(["deployed", "matched"]).nullable().describe("deployed: named by the active config bundle; matched: the robot's /map grid and geometry (v2 fingerprint) equal a stored version (several identical ones: the robot's own latest import, else a current version, else the newest)"),
        mapUnidentified: z
            .enum(["unverified", "unknown", "geometry_mismatch", "ambiguous"])
            .nullable()
            .describe("Why mapId is null although the robot reported a map fingerprint: unverified (legacy grid-only v1 fingerprint), unknown (no stored grid), geometry_mismatch (grid matches, resolution/origin differ), ambiguous (no longer reported)"),
        localized: z.boolean(),
        estop: z.object({ hardware: z.boolean(), software: z.boolean() }),
        teleopActive: z.boolean(),
        missionProgress: z.number().nullable(),
        reportedAt: Time,
    })
        .nullable()
        .describe("Last reported state; retained with its timestamp while offline"),
    activeMissionId: Id.nullable(),
    unresolved: z
        .object({ commandId: Id, missionId: Id.nullable(), purpose: z.string(), interfaceId: z.string(), error: ShortError.nullable(), createdAt: Time })
        .nullable()
        .describe("Oldest command whose outcome is `unknown`; new physical work is refused (OutcomeUnresolved) until it is reported or resolved"),
    activeErrorCount: z.number().int(),
    highestSeverity: Severity.nullable(),
    softwareVersion: z.string().nullable(),
    agentVersion: z.string().nullable(),
    configVersion: z.number().int().nullable(),
    reconciled: z.boolean().describe("False until post-connect reconciliation completes; commands are held until then"),
    control: ControlOwnerDtoSchema.nullable(),
    authority: RobotAuthorityDtoSchema,
    interfaces: z
        .object({ allowed: z.array(z.string()), bound: z.array(z.string()).nullable() })
        .describe("Registry interfaces in the robot's local allowlist, and those bound on the live session (null when offline)"),
    createdAt: Time,
});
export const RobotCredentialDtoSchema = z.object({
    robotId: Id,
    secret: z.string().describe("Shown exactly once; store on the robot"),
    generation: z.number().int(),
});
export const RobotCreatedSchema = z.object({ robot: RobotDtoSchema, credential: RobotCredentialDtoSchema });
export const TelemetryValueDtoSchema = z.object({
    interfaceId: z.string(),
    label: z.string(),
    unit: z.string(),
    value: Json.describe("Value at the registry metric's valuePath (null when the entry has no metric)"),
    data: Json.describe("The (projected) ROS message as JSON"),
    robotStamp: Time,
    receivedAt: Time,
    stale: z.boolean(),
    staleAfterMs: z.number().int().nullable(),
});
// ── error catalog ──
export const ErrorDefinitionDtoSchema = ErrorDefinitionInput.extend({ updatedAt: Time, updatedBy: z.string() });
export const TelemetryHistoryDtoSchema = z.object({
    windowMs: z.number().int(),
    resolutionMs: z.number().int(),
    retention: z.string().describe("How long history is kept and when it is lost"),
    series: z.array(z.object({ interfaceId: z.string(), label: z.string(), unit: z.string(), points: z.array(z.object({ at: Time, value: z.number() })) })),
});
export const TimelineItemDtoSchema = z.object({
    at: Time.describe("Platform clock"),
    kind: z.enum(["mission", "command", "error", "deployment", "connection", "control"]),
    title: z.string(),
    detail: z.string().nullable(),
    tone: z.enum(["info", "success", "warning", "danger"]),
    missionId: Id.nullable(),
    commandId: Id.nullable(),
    deploymentId: Id.nullable(),
    errorCode: z.string().nullable(),
});
export const TimelineDtoSchema = z.object({
    from: Time,
    to: Time,
    items: z.array(TimelineItemDtoSchema).describe("Newest first"),
    truncated: z.boolean().describe("More items exist in the window than `limit`"),
});
export const ActiveErrorDtoSchema = z.object({
    code: z.string(),
    instanceId: z.string(),
    raisedAt: Time.describe("Robot clock when the robot raised it. Simulators and robots without a synchronised clock can report implausible times; compare with firstSeenAt"),
    firstSeenAt: Time.describe("Platform clock when the platform first received it"),
    robotMessage: z.string().nullable(),
    priority: z.number().nullable(),
    definition: ErrorDefinitionDtoSchema.nullable(),
});
// ── missions & commands ──
export const CommandDtoSchema = z.object({
    id: Id.describe("commandId — also sent to the robot, which deduplicates on it"),
    robotId: Id,
    missionId: Id.nullable(),
    op: z.string(),
    interfaceId: z.string(),
    args: Json,
    purpose: z.string(),
    mode: CommandMode,
    authorityGeneration: z.number().int().describe("Authority generation it was issued under; the robot rejects older generations"),
    payloadSha256: z.string().describe("Payload identity; the robot rejects the same commandId with a different hash"),
    status: CommandStatus,
    visibility: Visibility,
    requestedBy: z.string(),
    createdAt: Time.describe("API accepted"),
    expiresAt: Time.describe("Admission expiry (robot will not start it later)"),
    ackDeadline: Time.describe("Un-acked past this while connected → `unknown`"),
    executionDeadline: NullableTime.describe("Informational; the robot enforces its own"),
    deadlineBreachedAt: NullableTime.describe("Robot reported the execution deadline passed — not an outcome"),
    dispatchedAt: NullableTime,
    acknowledgedAt: NullableTime.describe("Robot acknowledged"),
    startedAt: NullableTime.describe("Robot reported execution started"),
    finishedAt: NullableTime,
    cancelRequestedAt: NullableTime,
    cancelAcceptedAt: NullableTime.describe("Robot accepted the cancel request"),
    cancelRejectedAt: NullableTime.describe("Robot refused to cancel; the command keeps running"),
    cancelCompletedAt: NullableTime.describe("Robot confirmed the stop"),
    progress: z.number().nullable(),
    lastFeedback: Json,
    result: Json,
    error: ShortError.nullable(),
    conflict: z
        .object({ status: z.string(), error: ShortError.nullable(), at: Time, source: z.enum(["cmd_result", "reconcile"]) })
        .nullable()
        .describe("A later report that contradicted the recorded terminal outcome (kept for review, not applied)"),
});
export const MissionEventDtoSchema = z.object({ id: Id, at: Time, kind: z.string(), detail: Json });
export const MissionDtoSchema = z.object({
    id: Id,
    robotId: Id,
    workflowId: Id,
    workflowName: z.string(),
    params: Json,
    status: MissionStatus,
    visibility: Visibility.describe("`stale` while the robot is disconnected — not a failure"),
    requestedBy: z.string(),
    idempotencyKey: z.string().nullable(),
    createdAt: Time.describe("API accepted"),
    acknowledgedAt: NullableTime.describe("Robot acknowledged"),
    startedAt: NullableTime.describe("Execution started"),
    finishedAt: NullableTime,
    progress: z.number().nullable(),
    outcomeReason: z.string().nullable(),
    configPin: z
        .object({
        bundleVersion: z.number().int(),
        bundleSha256: z.string(),
        deploymentId: Id,
        mapId: Id,
        mapVersion: z.number().int(),
        workflowId: Id,
        workflowVersion: z.number().int(),
        workflowFingerprint: z.string(),
    })
        .nullable()
        .describe("Immutable configuration (bundle version + checksum, map, workflow) the mission is pinned to"),
    commands: z.array(CommandDtoSchema).optional().describe("Included on GET /missions/:id"),
    events: z.array(MissionEventDtoSchema).optional().describe("Included on GET /missions/:id"),
});
export const MapImportDtoSchema = z.object({
    id: Id,
    robotId: Id,
    siteId: Id,
    name: z.string(),
    mapId: Id.nullable().describe("Existing map receiving a version, or the map created on success"),
    mapVersion: z.number().int().nullable(),
    commandId: Id.describe("The map_export command sent to the robot"),
    status: MapImportStatus,
    error: ShortError.nullable(),
    requestedBy: z.string(),
    createdAt: Time,
    finishedAt: NullableTime,
});
export const RoutePlanDtoSchema = z.object({
    id: Id,
    mapId: Id,
    mapVersion: z.number().int(),
    status: RoutePlanStatus,
    staleReason: z.string().nullable().describe("Why the plan no longer matches the map's version or waypoints; null when current"),
    pairs: z.array(z.object({ pair: z.string().describe('"<from>-><to>" waypoint names'), ok: z.boolean(), seconds: z.number().nullable(), error: z.string().nullable() })),
    refused: z.string().nullable().describe("Why the planner wrote nothing (status refused)"),
    error: z.string().nullable().describe("Why the planner could not run (status failed)"),
    nodeCount: z.number().int(),
    edgeCount: z.number().int(),
    harnessRevision: z.string().nullable().describe("tcr commit of global_path_harness that planned it"),
    imported: z.boolean().describe("Uploaded as an already planned route_network.json (POST …/route-plans/import) rather than planned by the platform"),
    durationS: z.number().nullable(),
    requestedBy: z.string(),
    createdAt: Time,
    startedAt: NullableTime,
    finishedAt: NullableTime,
});
export const PlannedRouteDtoSchema = z.object({
    edgeId: z.string(),
    from: z.string(),
    to: z.string(),
    x: z.array(z.number()),
    y: z.array(z.number()),
    travelTimeS: z.number().nullable(),
    minClearanceM: z.number().nullable(),
});
const Vec3 = z.tuple([z.number(), z.number(), z.number()]);
export const MapLayerDtoSchema = z.object({
    id: Id,
    mapId: Id,
    mapVersion: z.number().int(),
    kind: MapLayerKind,
    format: PointCloudFormat,
    originalName: z.string(),
    sha256: z.string().describe("sha256 of the uploaded file"),
    sizeBytes: z.number().int(),
    points: z.number().int().describe("Points in the source cloud"),
    previewPoints: z.number().int(),
    voxelSize: z.number().describe("Voxel size of the preview decimation (m)"),
    bounds: z.object({ min: Vec3, max: Vec3 }),
    hasIntensity: z.boolean(),
    frame: z.literal("map"),
    createdAt: Time,
    createdBy: z.string(),
});
export const CalibrationRunDtoSchema = z.object({
    id: Id,
    robotId: Id,
    type: CalibrationType,
    commandId: Id,
    status: CommandStatus,
    requestedBy: z.string(),
    startedAt: NullableTime,
    finishedAt: NullableTime,
    progress: z.number().nullable(),
    result: Json,
    error: ShortError.nullable(),
    createdAt: Time,
});
export const RecoveryAttemptDtoSchema = z.object({
    id: Id,
    robotId: Id,
    errorCode: z.string(),
    instanceId: z.string().nullable(),
    commandId: Id,
    status: CommandStatus,
    requestedBy: z.string(),
    createdAt: Time,
    finishedAt: NullableTime,
});
export const ControlLeaseDtoSchema = ControlOwnerDtoSchema.extend({ robotId: Id, createdAt: Time, releasedAt: NullableTime });
export const ArmJogSessionDtoSchema = z.object({
    robotId: Id,
    leaseId: Id,
    authorityGeneration: z.number().int().positive(),
    visibility: ArmJogVisibility,
    confirmedBy: Id,
    confirmedAt: Time,
});
// ── maps ──
export const MapVersionDtoSchema = z.object({
    id: Id,
    mapId: Id,
    version: z.number().int(),
    resolution: z.number().describe("metres per pixel"),
    origin: z.tuple([z.number(), z.number(), z.number()]).describe("[x, y, yaw] of the lower-left pixel (Nav2)"),
    width: z.number().int(),
    height: z.number().int(),
    negate: z.boolean(),
    occupiedThresh: z.number(),
    freeThresh: z.number(),
    sha256: z.string(),
    createdAt: Time,
    createdBy: z.string(),
    edit: z
        .object({ parentVersion: z.number().int(), freeCells: z.number().int(), wallCells: z.number().int() })
        .nullable()
        .describe("Painted in the editor: the version it was painted over and the cells made free / wall; null for uploads and imports"),
});
export const MapDtoSchema = z.object({
    id: Id,
    siteId: Id,
    name: z.string(),
    description: z.string(),
    currentVersion: MapVersionDtoSchema.nullable(),
    createdAt: Time,
    updatedAt: Time,
});
export const WaypointDtoSchema = z.object({
    id: Id,
    mapId: Id,
    name: z.string(),
    type: WaypointType,
    pose: Pose2D,
    properties: z.record(z.unknown()),
    updatedAt: Time,
});
export const PathDtoSchema = z.object({
    id: Id,
    mapId: Id,
    name: z.string(),
    controlPoints: z.array(z.object({ x: z.number(), y: z.number() })),
    spline: SplineParams,
    startWaypointId: Id.nullable(),
    endWaypointId: Id.nullable(),
    headingMode: z.enum(["tangent", "fixed"]),
    fixedHeading: z.number().nullable(),
    speedLimit: z.number().nullable(),
    version: z.number().int(),
    lengthM: z.number(),
    updatedAt: Time,
});
export const WorkflowDtoSchema = z.object({
    id: Id,
    mapId: Id,
    name: z.string(),
    description: z.string(),
    behaviorRef: z.string(),
    steps: z.array(WorkflowStep),
    version: z.number().int(),
    updatedAt: Time,
});
export const ValidationIssueDtoSchema = z.object({ severity: z.enum(["error", "warning"]), path: z.string(), message: z.string() });
const DeploymentItemRefSchema = z.object({ id: Id, name: z.string() });
const DeploymentItemChangesSchema = z.object({
    added: z.array(DeploymentItemRefSchema),
    removed: z.array(DeploymentItemRefSchema),
    changed: z.array(DeploymentItemRefSchema),
    unchanged: z.number().int(),
});
export const DeploymentPreviewDtoSchema = z.object({
    mapId: Id,
    mapName: z.string(),
    mapVersion: z.number().int().nullable().describe("The map's current version; null when none is uploaded"),
    active: z
        .object({ deploymentId: Id, bundleVersion: z.number().int(), mapId: Id, mapName: z.string().nullable(), mapVersion: z.number().int(), activatedAt: Time.nullable() })
        .nullable()
        .describe("The robot's active configuration as confirmed by the robot; null when none is known"),
    activeContentsUnavailable: z.boolean().describe("The active bundle could not be read; every object is then listed as added"),
    replacesMap: z.boolean().describe("Deploying would switch the robot to a different map"),
    mapVersionChanged: z.boolean(),
    waypoints: DeploymentItemChangesSchema,
    paths: DeploymentItemChangesSchema,
    workflows: DeploymentItemChangesSchema,
    identical: z.boolean().describe("Nothing differs from the active configuration"),
});
export const ConfigDeploymentDtoSchema = z.object({
    id: Id,
    robotId: Id,
    mapId: Id,
    mapVersion: z.number().int(),
    bundleVersion: z.number().int(),
    sha256: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    status: ConfigDeploymentStatus,
    issues: z.array(ValidationIssueDtoSchema),
    commandId: Id.nullable().describe("config_stage command (transfer, validation, staging)"),
    activateCommandId: Id.nullable().describe("Latest config_activate command"),
    compatibility: CompatibilityReport.nullable().describe("What the robot checked at staging"),
    stagedAt: NullableTime,
    activatedAt: NullableTime.describe("Robot acknowledged the active version and checksum"),
    deferredReason: z.string().nullable().describe("Why activation is waiting for a safe boundary"),
    rollbackOf: Id.nullable(),
    note: z.string(),
    requestedBy: z.string(),
    createdAt: Time,
    finishedAt: NullableTime,
    error: ShortError.nullable(),
});
// ── registry, audit, diagnostics ──
export const RegistryEntryDtoSchema = z.object({ entry: RegistryEntry, updatedAt: Time, updatedBy: z.string() });
export const RegistryDtoSchema = z.object({ version: z.number().int(), hash: z.string(), entries: z.array(RegistryEntryDtoSchema) });
export const AuditEventDtoSchema = z.object({
    id: Id,
    at: Time,
    actorId: Id.nullable(),
    actorName: z.string(),
    actorKind: z.enum(["user", "api_token", "robot", "system"]),
    action: z.string(),
    robotId: Id.nullable(),
    missionId: Id.nullable(),
    commandId: Id.nullable(),
    summary: Json,
    result: z.enum(["ok", "denied", "error"]),
});
export const DiagnosticArtifactDtoSchema = z.object({
    id: Id,
    robotId: Id,
    kind: z.string(),
    filename: z.string(),
    sizeBytes: z.number().int(),
    sha256: z.string(),
    missionId: Id.nullable(),
    commandId: Id.nullable(),
    createdAt: Time,
});
export const RobotTokenResponseSchema = z.object({ token: z.string(), expiresAt: z.number().int().describe("epoch ms") });
const assertSame = () => undefined;
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
assertSame();
//# sourceMappingURL=api-schemas.js.map